import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import { Calendar, IndianRupee, Users, TrendingUp, TrendingDown, Calendar as CalendarIcon } from 'lucide-react';
import axiosInstance from '../networking/axiosconfig';
import axios from 'axios';

const LOCAL_API_BASE_URL = 'http://attendance.inurum.com/api';

const extractMonthlySalary = (response) => {
  const payload = response?.data;
  if (!payload) return null;

  const coerce = (value) => {
    if (value === undefined || value === null) return null;
    const num = parseFloat(value);
    return Number.isNaN(num) ? null : num;
  };

  if (typeof payload.monthlySalary === 'number' || typeof payload.monthlySalary === 'string') {
    return coerce(payload.monthlySalary);
  }

  if (typeof payload.monthly_salary === 'number' || typeof payload.monthly_salary === 'string') {
    return coerce(payload.monthly_salary);
  }

  if (payload.salary !== undefined) {
    if (typeof payload.salary === 'number' || typeof payload.salary === 'string') {
      return coerce(payload.salary);
    }
    const salaryObj = payload.salary || {};
    if (salaryObj.monthly_salary !== undefined) return coerce(salaryObj.monthly_salary);
    if (salaryObj.monthlySalary !== undefined) return coerce(salaryObj.monthlySalary);
  }

  if (Array.isArray(payload.data) && payload.data.length > 0) {
    const latestRecord = payload.data[0];
    if (latestRecord.monthly_salary !== undefined) return coerce(latestRecord.monthly_salary);
    if (latestRecord.monthlySalary !== undefined) return coerce(latestRecord.monthlySalary);
  }

  return null;
};

const Dashboard = () => {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const currentDate = new Date();
  const currentCalendarYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();
  const previousMonthIndex = currentMonthIndex === 0 ? 11 : currentMonthIndex - 1;
  const previousYear = currentMonthIndex === 0 ? currentCalendarYear - 1 : currentCalendarYear;
  const defaultMonthName = monthNames[previousMonthIndex];

  const [selectedMonth, setSelectedMonth] = useState(defaultMonthName);
  const [selectedYear, setSelectedYear] = useState(previousYear);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publicHolidays, setPublicHolidays] = useState({});
  const [totalPayroll, setTotalPayroll] = useState(null);
  const [totalPayrollLoading, setTotalPayrollLoading] = useState(false);

  const financialYearMonths = [
    'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December', 'January', 'February', 'March'
  ];

  const buildYearOptions = () => {
    const startYear = currentCalendarYear - 10;
    const endYear = currentCalendarYear + 50;
    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };

  const availableYears = buildYearOptions();

  const getHolidayInfo = (date, month, year) =>
    publicHolidays[year]?.[month]?.[date] || null;

  const getMonthNumber = (monthName) => monthNames.indexOf(monthName) + 1;

  const getDaysInMonth = (month, year) => {
    const monthIndex = monthNames.indexOf(month);
    return new Date(year, monthIndex + 1, 0).getDate();
  };

  const getDayName = (date, month, year) => {
    const monthIndex = monthNames.indexOf(month);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dateObj = new Date(year, monthIndex, date);
    return dayNames[dateObj.getDay()];
  };

  const isPublicHoliday = (date, month, year) => Boolean(getHolidayInfo(date, month, year));

  const isSunday = (date, month, year) => {
    return getDayName(date, month, year) === 'Sun';
  };

  const getWorkingDaysInMonth = (month, year) => {
    const totalDays = getDaysInMonth(month, year);
    let workingDays = 0;
    for (let day = 1; day <= totalDays; day++) {
      if (isSunday(day, month, year)) continue;
      if (isPublicHoliday(day, month, year)) continue;
      workingDays++;
    }
    return workingDays;
  };

  const getMonthYear = (month) => {
    const monthIndex = financialYearMonths.indexOf(month);
    return monthIndex <= 8 ? selectedYear : selectedYear + 1;
  };

  const calculateAttendanceStats = (attendance) => {
    let present = 0, cl = 0, pl = 0, absent = 0, sunday = 0, holiday = 0;

    attendance.forEach(day => {
      if (day.status === 'present') present++;
      else if (day.status === 'cl') cl++;
      else if (day.status === 'pl') pl++;
      else if (day.status === 'absent') absent++;
      else if (day.status === 'sunday') sunday++;
      else if (day.status === 'holiday') holiday++;
    });

    return { present, cl, pl, absent, sunday, holiday };
  };

  const getQuarterLeaveStatus = (employeeData, month) => {
    const quarterMap = {
      'April': ['April', 'May', 'June'],
      'May': ['April', 'May', 'June'],
      'June': ['April', 'May', 'June'],
      'July': ['July', 'August', 'September'],
      'August': ['July', 'August', 'September'],
      'September': ['July', 'August', 'September'],
      'October': ['October', 'November', 'December'],
      'November': ['October', 'November', 'December'],
      'December': ['October', 'November', 'December'],
      'January': ['January', 'February', 'March'],
      'February': ['January', 'February', 'March'],
      'March': ['January', 'February', 'March'],
    };

    const quarterMonths = quarterMap[month];
    let totalCL = 0, totalPL = 0, totalUnpaid = 0;

    quarterMonths.forEach(m => {
      if (employeeData[m]) {
        totalCL += employeeData[m].cl || 0;
        totalPL += employeeData[m].pl || 0;
        totalUnpaid += employeeData[m].unpaid || 0;
      }
    });

    return { totalCL, totalPL, totalUnpaid, quarterMonths };
  };

  const calculateSalaryFromAttendance = (employee, month) => {
    const monthData = employee.monthlyData?.[month];
    if (!monthData) {
      // If no month data, use baseSalary (should be set from API)
      const netSalary = employee.baseSalary || 0;
      return {
        netSalary,
        deduction: 0,
        present: 0,
        cl: 0,
        pl: 0,
        unpaid: 0,
        sunday: 0,
        holiday: 0,
        absent: 0
      };
    }

    const stats = calculateAttendanceStats(monthData.attendance || []);
    const quarterStatus = getQuarterLeaveStatus(employee.monthlyData || {}, month);

    let unpaidLeaves = 0;
    const totalPaidLeavesInQuarter = quarterStatus.totalCL + quarterStatus.totalPL;
    const quarterlyPaidLeaveLimit = 4;

    if (totalPaidLeavesInQuarter > quarterlyPaidLeaveLimit) {
      unpaidLeaves = totalPaidLeavesInQuarter - quarterlyPaidLeaveLimit;
    }
    unpaidLeaves += stats.absent;

    // Use netSalary from monthData if available, otherwise fallback to baseSalary
    // Ensure we always return a valid number
    const netSalary = monthData.netSalary !== undefined && monthData.netSalary !== null
      ? monthData.netSalary
      : (employee.baseSalary || 0);
    const deduction = monthData.deduction ?? 0;

    return {
      netSalary: typeof netSalary === 'number' ? netSalary : 0,
      deduction: typeof deduction === 'number' ? deduction : 0,
      present: stats.present,
      cl: stats.cl,
      pl: stats.pl,
      unpaid: unpaidLeaves,
      sunday: stats.sunday,
      holiday: stats.holiday,
      absent: stats.absent
    };
  };

  const fetchAttendanceFromApi = async (userId, month, year) => {
    try {
      const response = await axios.get(`${LOCAL_API_BASE_URL}/get`, {
        params: { userId, month, year }
      });
      const data = response?.data;
      if (Array.isArray(data?.data)) return data.data;
      if (Array.isArray(data?.attendance)) return data.attendance;
      return [];
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error(`Failed to fetch attendance for user ${userId}:`, error.message || error);
      }
      return [];
    }
  };

  const fetchSalarySummaryFromApi = async (userId, month, year) => {
    try {
      const response = await axios.get(`${LOCAL_API_BASE_URL}/get/netsalary`, {
        params: { userId, month, year }
      });
      const rows = response?.data?.data;
      if (Array.isArray(rows) && rows.length > 0) {
        const latest = rows[0];
        const parseNumber = (value) => {
          if (value === undefined || value === null) return null;
          const num = parseFloat(value);
          return Number.isNaN(num) ? null : num;
        };
        return {
          netSalary: parseNumber(latest.net_salary ?? latest.netSalary),
          deduction: parseNumber(latest.salary_cut ?? latest.salaryCut),
          perDaySalary: parseNumber(latest.per_day_salary ?? latest.perDaySalary),
          monthlySalary: parseNumber(latest.monthly_salary ?? latest.monthlySalary),
          raw: latest
        };
      }
      return null;
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error(`Failed to fetch salary summary for user ${userId}:`, error.message || error);
      }
      return null;
    }
  };

  const fetchTotalPayrollFromApi = async (month, year) => {
    try {
      setTotalPayrollLoading(true);
      const response = await axios.post(`${LOCAL_API_BASE_URL}/salary/total-payroll`, {
        month,
        year
      });

      const payrollData = response?.data;
      // Handle different possible response structures
      const totalPayrollValue = payrollData?.total_payroll ??
        payrollData?.totalPayroll ??
        payrollData?.data?.total_payroll ??
        payrollData?.data?.totalPayroll ??
        payrollData?.amount ??
        0;

      const parsedValue = parseFloat(totalPayrollValue);
      setTotalPayroll(isNaN(parsedValue) ? 0 : parsedValue);
    } catch (error) {
      console.error('Failed to fetch total payroll:', error);
      // On error, fallback to calculated value
      setTotalPayroll(null);
    } finally {
      setTotalPayrollLoading(false);
    }
  };

  const buildDefaultAttendance = (monthName, year) => {
    const daysInMonth = getDaysInMonth(monthName, year);
    return Array.from({ length: daysInMonth }, (_, index) => {
      const date = index + 1;
      if (isSunday(date, monthName, year)) {
        return { date, status: 'sunday', dayName: 'Sun', isHoliday: false };
      }
      const holidayInfo = getHolidayInfo(date, monthName, year);
      if (holidayInfo) {
        return {
          date,
          status: 'holiday',
          dayName: getDayName(date, monthName, year),
          isHoliday: true,
          holidayName: holidayInfo.name
        };
      }
      return {
        date,
        status: 'present',
        dayName: getDayName(date, monthName, year),
        isHoliday: false
      };
    });
  };

  const transformApiAttendance = (records, monthName, year) => {
    return records
      .map((record) => {
        const rawDate = record.date || record.attendance_date;
        if (!rawDate) return null;
        const parsedDate = new Date(rawDate);
        if (Number.isNaN(parsedDate.getTime())) return null;
        const day = parsedDate.getDate();
        const status = (record.status || '').toLowerCase();
        const holidayInfo = getHolidayInfo(day, monthName, year);
        const recordHolidayName = record.holiday_name || record.holidayName || record.name;
        const finalHolidayName = recordHolidayName || holidayInfo?.name;
        const isHolidayDay = status === 'holiday' || Boolean(holidayInfo);
        return {
          date: day,
          status,
          dayName: getDayName(day, monthName, year),
          isHoliday: isHolidayDay,
          holidayName: isHolidayDay ? (finalHolidayName || 'Holiday') : undefined
        };
      })
      .filter(Boolean);
  };

  const attachAttendanceToEmployees = async (employeeList, monthName, year) => {
    const monthNumber = getMonthNumber(monthName);

    const updatedEmployees = await Promise.all(
      employeeList.map(async (emp) => {
        const apiRecords = await fetchAttendanceFromApi(emp.id, monthNumber, year);
        const attendanceEntries = apiRecords.length
          ? transformApiAttendance(apiRecords, monthName, year)
          : buildDefaultAttendance(monthName, year);

        const stats = calculateAttendanceStats(attendanceEntries);
        const salarySummary = await fetchSalarySummaryFromApi(emp.id, monthNumber, year);
        const previousMonthData = emp.monthlyData?.[monthName] || {};

        return {
          ...emp,
          monthlyData: {
            ...emp.monthlyData,
            [monthName]: {
              ...(emp.monthlyData?.[monthName] || {}),
              attendance: attendanceEntries,
              present: stats.present,
              cl: stats.cl,
              pl: stats.pl,
              absent: stats.absent,
              sunday: stats.sunday,
              holiday: stats.holiday,
              netSalary: salarySummary?.netSalary ?? previousMonthData.netSalary ?? emp.baseSalary,
              deduction: salarySummary?.deduction ?? previousMonthData.deduction ?? 0,
              serverSalary: salarySummary
            }
          }
        };
      })
    );

    return updatedEmployees;
  };

  const buildHolidayMap = (records = []) => {
    const map = {};
    records.forEach((holiday) => {
      const dateObj = new Date(holiday.date);
      if (Number.isNaN(dateObj.getTime())) return;
      const year = dateObj.getFullYear();
      const monthName = monthNames[dateObj.getMonth()];
      const day = dateObj.getDate();

      if (!map[year]) map[year] = {};
      if (!map[year][monthName]) map[year][monthName] = {};
      map[year][monthName][day] = {
        day,
        name: holiday.holiday_name || holiday.holidayName || holiday.name || 'Holiday',
        raw: holiday
      };
    });
    return map;
  };

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const response = await axios.get(`${LOCAL_API_BASE_URL}/get/holiday`);
        const records = response.data?.data || [];
        setPublicHolidays(buildHolidayMap(records));
      } catch (err) {
        console.error('Failed to fetch holidays:', err);
      }
    };

    fetchHolidays();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      const token = localStorage.getItem("token");

      if (!token || !token.trim()) {
        setError('Please login to access this page.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await axiosInstance.get('/api/users', {
          headers: {
            Authorization: `Bearer ${token.trim()}`
          }
        });

        const employeesData = response.data?.data || [];

        // Filter out inactive employees (is_active = 1 means inactive)
        const activeEmployeesData = employeesData.filter(emp => emp.is_active !== 1);

        const transformedEmployees = await Promise.all(
          activeEmployeesData.map(async (emp) => {
            let avatar = '👤';
            if (emp.gender === 'Male') avatar = '👨';
            else if (emp.gender === 'Female') avatar = '👩';

            // Try to get baseSalary from employee data first (check multiple fields)
            let baseSalary = emp.base_salary || emp.monthly_salary || 0;
            try {
              const salaryResponse = await axios.get(`${LOCAL_API_BASE_URL}/salary/get`, {
                params: { userId: emp.user_id_PK }
              });
              const savedSalary = extractMonthlySalary(salaryResponse);
              if (savedSalary) {
                const parsedSalary = parseFloat(savedSalary);
                if (!isNaN(parsedSalary) && parsedSalary > 0) {
                  baseSalary = parsedSalary;
                }
              }
            } catch (salaryErr) {
              if (salaryErr?.response?.status !== 404) {
                console.error(`Error fetching salary for user ${emp.user_id_PK}:`, salaryErr.message);
              }
            }

            // Debug: Log if baseSalary is 0 or missing
            if (!baseSalary || baseSalary === 0) {
              console.warn(`Employee ${emp.user_id_PK} (${emp.first_name} ${emp.last_name}) has no baseSalary set`);
            }

            return {
              id: emp.user_id_PK,
              name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Unknown',
              avatar: emp.user_profile_img || avatar,
              baseSalary,
              monthlyData: {},
              email: emp.email,
              designation: emp.designation_name,
              department: emp.department_name,
              isActive: emp.is_active === 0
            };
          })
        );

        const yearForSelectedMonth = getMonthYear(selectedMonth);
        const employeesWithAttendance = await attachAttendanceToEmployees(
          transformedEmployees,
          selectedMonth,
          yearForSelectedMonth
        );

        setEmployees(employeesWithAttendance);
      } catch (err) {
        console.error('Error fetching employees:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Failed to load employees.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    if (!employees.length) return;
    const year = getMonthYear(selectedMonth);

    const refreshAttendance = async () => {
      const updated = await attachAttendanceToEmployees(employees, selectedMonth, year);
      setEmployees(updated);
    };

    refreshAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, publicHolidays]);

  useEffect(() => {
    const monthNumber = getMonthNumber(selectedMonth);
    const year = getMonthYear(selectedMonth);
    fetchTotalPayrollFromApi(monthNumber, year);
  }, [selectedMonth, selectedYear]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <p className="text-red-600 font-semibold mb-4">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Navbar />
      <div className="max-w-7xl mx-auto p-6">

        <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 rounded-2xl shadow-2xl p-8 mb-6 text-white">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3 mb-2">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <IndianRupee className="text-white" size={36} />
                </div>
                Employee Salary Management System
              </h1>
              <p className="text-blue-100 mt-2 text-lg">Financial Year: April 2024 - March 2025</p>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-sm px-6 py-4 rounded-xl border border-white border-opacity-30">
              <div className="flex items-center gap-2 mb-2">
                <CalendarIcon size={18} />
                <p className="text-sm font-semibold">
                  Working Days: {getWorkingDaysInMonth(selectedMonth, getMonthYear(selectedMonth))} days
                </p>
              </div>
              <p className="text-xs text-blue-100">{selectedMonth} {getMonthYear(selectedMonth)}</p>
              <div className="mt-2 pt-2 border-t border-white border-opacity-20">
                <p className="text-xs text-blue-100">Quarterly Limit: 4 Paid Leaves (CL+PL)</p>
              </div>
            </div>
          </div>
        </div>
        {/* Month & Year Selector */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100">
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
            <Calendar className="text-blue-600" size={20} />
            <span>Select Month & Year</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-2">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white hover:border-gray-300 cursor-pointer"
              >
                {financialYearMonths.map((month) => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-2">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white hover:border-gray-300 cursor-pointer"
              >
                {availableYears.map((yearOption) => (
                  <option key={yearOption} value={yearOption}>
                    {yearOption}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Summary Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-800">Summary for {selectedMonth} {getMonthYear(selectedMonth)}</h3>
              <p className="text-sm text-gray-500 mt-1">Complete overview of salary and attendance</p>
            </div>
            <div className="hidden md:flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
              <CalendarIcon className="text-blue-600" size={20} />
              <span className="text-sm font-semibold text-blue-700">{selectedMonth} {getMonthYear(selectedMonth)}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Total Employees Card */}
            <div className="group relative bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <Users size={24} />
                  </div>
                  <TrendingUp className="opacity-80" size={20} />
                </div>
                <p className="text-sm opacity-90 mb-2 font-medium">Total Employees</p>
                <p className="text-4xl font-bold mb-1">{employees.length}</p>
                <p className="text-xs opacity-75">Active workforce</p>
              </div>
            </div>

            {/* Total Payroll Card */}
            <div className="group relative bg-gradient-to-br from-green-500 via-green-600 to-emerald-700 text-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <IndianRupee size={24} />
                  </div>
                  <TrendingUp className="opacity-80" size={20} />
                </div>
                <p className="text-sm opacity-90 mb-2 font-medium">Total Payroll</p>
                <p className="text-4xl font-bold mb-1">
                  {totalPayrollLoading ? (
                    <span className="text-2xl">Loading...</span>
                  ) : (
                    `₹${(totalPayroll !== null ? totalPayroll : employees.reduce((sum, emp) => {
                      const salaryData = calculateSalaryFromAttendance(emp, selectedMonth);
                      // Use netSalary if available, otherwise use baseSalary, ensure it's a number
                      const salary = salaryData.netSalary || emp.baseSalary || 0;
                      return sum + (typeof salary === 'number' ? salary : 0);
                    }, 0)).toLocaleString()}`
                  )}
                </p>
                <p className="text-xs opacity-75">Net salary distribution</p>
              </div>
            </div>

            {/* Total Deductions Card */}
            <div className="group relative bg-gradient-to-br from-red-500 via-red-600 to-rose-700 text-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <TrendingDown size={24} />
                  </div>
                  <TrendingDown className="opacity-80" size={20} />
                </div>
                <p className="text-sm opacity-90 mb-2 font-medium">Total Deductions</p>
                <p className="text-4xl font-bold mb-1">
                  ₹{employees.reduce((sum, emp) => sum + calculateSalaryFromAttendance(emp, selectedMonth).deduction, 0).toLocaleString()}
                </p>
                <p className="text-xs opacity-75">Salary cuts</p>
              </div>
            </div>

            {/* Avg Present Days Card */}
            <div className="group relative bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-700 text-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <CalendarIcon size={24} />
                  </div>
                  <TrendingUp className="opacity-80" size={20} />
                </div>
                <p className="text-sm opacity-90 mb-2 font-medium">Avg Present Days</p>
                <p className="text-4xl font-bold mb-1">
                  {employees.length > 0 ? Math.round(employees.reduce((sum, emp) => sum + calculateSalaryFromAttendance(emp, selectedMonth).present, 0) / employees.length) : 0}
                </p>
                <p className="text-xs opacity-75">Average attendance days</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
